import React, { useState } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { ICampaignResults, QuestionType, RatingScale } from '../../interface/ICampaign';
import { formatDate } from '../../utils/dateUtils';
import style from './CampaignResults.module.scss';

interface CampaignResultsProps {
	results?: ICampaignResults;
	onClose: () => void;
	onPickWinners?: () => void;
	isLoading?: boolean;
}

export default function CampaignResults({ results, onClose, onPickWinners, isLoading = false }: CampaignResultsProps): JSX.Element {
	const [expandedSubmission, setExpandedSubmission] = useState<number | null>(null);
	
	if (isLoading || !results) {
		return (
			<div className={style.ResultsContainer}>
				<Box className={style.LoadingState}>
					<CircularProgress />
				</Box>
			</div>
		);
	}

	const { campaign, totalResponses, questionResults, winners } = results;

	// Calculate overall score including both rating and yes/no questions
	const calculateOverallScore = (): number | null => {
		let totalScore = 0;
		let questionCount = 0;
		
		questionResults.forEach(result => {
			// Include rating questions (1-5 scale)
			if (result.questionType === QuestionType.RATING && result.averageRating !== undefined) {
				totalScore += result.averageRating;
				questionCount++;
			}
			
			// Include yes/no questions (convert percentage to 1-5 scale)
			// Formula: 1 + (yesPercentage / 100) * 4
			// 100% Yes → 5.0, 75% Yes → 4.0, 50% Yes → 3.0, 25% Yes → 2.0, 0% Yes → 1.0
			if (result.questionType === QuestionType.YES_NO && result.yesPercentage !== undefined) {
				const yesNoScore = 1 + (result.yesPercentage / 100) * 4;
				totalScore += yesNoScore;
				questionCount++;
			}
			
			// Comment questions are excluded from score calculation
		});
		
		return questionCount > 0 ? totalScore / questionCount : null;
	};

	const overallAverageRating = calculateOverallScore();

	const getRatingLabel = (rating: number): string => {
		const labels = {
			[RatingScale.POOR]: 'Poor',
			[RatingScale.FAIR]: 'Fair',
			[RatingScale.AVERAGE]: 'Average',
			[RatingScale.GOOD]: 'Good',
			[RatingScale.EXCELLENT]: 'Excellent'
		};
		return labels[rating as RatingScale] || '';
	};

	// Check if campaign has ended (compare dates only, not time)
	const campaignHasEnded = () => {
		const now = new Date();
		const todayStr = now.toISOString().split('T')[0];
		const endDateStr = campaign.endDate.split('T')[0];
		return todayStr > endDateStr;
	};

	const canPickWinners =
		campaign.reward?.enabled &&
		campaign.reward?.numberOfWinners > 0 &&
		!winners?.length &&
		(results?.rewardOptInCount || 0) > 0 &&
		campaignHasEnded();
	console.log('Can Pick Winners:', winners);
	return (
		<div className={style.ResultsContainer}>
			<div className={style.Header}>
				<div className={style.HeaderTop}>
					<button onClick={onClose} className={style.BackButton}>
						Back
					</button>
					{campaign.reward?.enabled && onPickWinners && (
						<button 
							onClick={onPickWinners} 
							className={style.PickWinnerButton}
							disabled={!canPickWinners}
							title={!canPickWinners ? "Cannot pick winners yet" : "Pick winners randomly"}
							style={{ backgroundColor: '#446388', color: 'white' }}
						>
							Pick a Winner
						</button>
					)}
				</div>
				<h2>Campaign Results: {campaign.title}</h2>
			</div>

				<div className={style.CampaignSummary}>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Start Date:</span>
						<span className={style.Value}>{formatDate(campaign.startDate, false)}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>End Date:</span>
						<span className={style.Value}>{formatDate(campaign.endDate, false)}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Total Responses:</span>
						<span className={style.Value}>{totalResponses}</span>
					</div>
					<div className={style.SummaryItem}>
						<span className={style.Label}>Questions:</span>
						<span className={style.Value}>{campaign.questions.length}</span>
					</div>				{overallAverageRating !== null && (
					<div className={style.SummaryItem}>
						<span className={style.Label}>Score:</span>
						<span className={style.Value} style={{ color: '#446388', fontWeight: 'bold' }}>
							{overallAverageRating.toFixed(2)} / 5
						</span>
					</div>
				)}				</div>

				<div className={style.ResultsContent}>
					<h3>Question Results</h3>
					
					{questionResults.filter(result => result.questionType !== QuestionType.COMMENT).map((result, index) => (
						<div key={result.questionId} className={style.QuestionResult}>
							<div className={style.QuestionHeader}>
								<span className={style.QuestionNumber}>#{index + 1}</span>
								<span className={style.QuestionText}>{result.questionText}</span>
							</div>

							{result.questionType === QuestionType.YES_NO ? (
								<div className={style.YesNoResults}>
									<div className={style.AverageRating}>
										<span className={style.RatingScore}>
											{result.yesPercentage !== undefined 
												? (1 + (result.yesPercentage / 100) * 4).toFixed(2) 
												: 'N/A'}
										</span>
										<span className={style.RatingLabel}>Score</span>
									</div>
									<div className={style.YesNoDistribution}>
										<div className={style.ResultBar}>
											<div className={style.BarLabel}>
												<span>Yes</span>
												<span>{result.yesCount} ({result.yesPercentage?.toFixed(1)}%)</span>
											</div>
											<div className={style.BarContainer}>
												<div
													className={`${style.Bar} ${style.YesBar}`}
													style={{ width: `${result.yesPercentage}%` }}
												/>
											</div>
										</div>
										<div className={style.ResultBar}>
											<div className={style.BarLabel}>
												<span>No</span>
												<span>{result.noCount} ({result.noPercentage?.toFixed(1)}%)</span>
											</div>
											<div className={style.BarContainer}>
												<div
													className={`${style.Bar} ${style.NoBar}`}
													style={{ width: `${result.noPercentage}%` }}
												/>
											</div>
										</div>
									</div>
								</div>
							) : result.questionType === QuestionType.COMMENT ? (
								<div className={style.CommentResults}>
									<div className={style.CommentCount}>
										<span className={style.Count}>{result.comments?.length || 0}</span> &nbsp;
										<span className={style.Label}>Comments</span>
									</div>
									{result.comments && result.comments.length > 0 ? (
										<div className={style.CommentsList}>
											{result.comments.map((comment, idx) => (
												<div key={idx} className={style.CommentItem}>
													<div className={style.CommentNumber}>#{idx + 1}</div>
													<div className={style.CommentText}>{comment}</div>
												</div>
											))}
										</div>
									) : (
										<div className={style.NoComments}>
											<p>No comments submitted yet.</p>
										</div>
									)}
								</div>
							) : (
								<div className={style.RatingResults}>
									<div className={style.AverageRating}>
										<span className={style.RatingScore}>{result.averageRating?.toFixed(2)}</span>
										<span className={style.RatingLabel}>Score</span>
									</div>
									<div className={style.RatingDistribution}>
										{[5, 4, 3, 2, 1].map((rating) => {
											const count = result.ratingDistribution?.[rating as RatingScale] || 0;
											const percentage = result.totalAnswers > 0 
												? (count / result.totalAnswers) * 100 
												: 0;

											return (
												<div key={rating} className={style.RatingRow}>
													<span className={style.RatingNumber}>{rating}</span>
													<span className={style.RatingText}>{getRatingLabel(rating)}</span>
													<div className={style.BarContainer}>
														<div
															className={`${style.Bar} ${style.RatingBar}`}
															style={{ width: `${percentage}%` }}
														/>
													</div>
													<span className={style.RatingCount}>
														{count} ({percentage.toFixed(1)}%)
													</span>
												</div>
											);
										})}
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			{/* Individual Responses Section */}
			{results.submissions && results.submissions.length > 0 && (
				<div className={style.IndividualResponsesSection}>
					<h3>Individual Responses</h3>
					<div className={style.SubmissionsList}>
						{results.submissions.map((submission, index) => {
							const isExpanded = expandedSubmission === submission.id;
							const submittedDate = new Date(submission.submittedAt);
							
							return (
								<div key={submission.id} className={style.SubmissionItem}>
									<div 
										className={style.SubmissionHeader}
										onClick={() => setExpandedSubmission(isExpanded ? null : submission.id)}
									>
										<div className={style.SubmissionInfo}>
											<span className={style.SubmissionNumber}>#{index + 1}</span>
											<div className={style.SubmitterDetails}>
												<span className={style.SubmitterName}>
													{submission.name || 'Anonymous'}
												</span>
												{submission.email && (
													<span className={style.SubmitterEmail}>{submission.email}</span>
												)}
											</div>
										</div>
										<div className={style.SubmissionMeta}>
											<span className={style.SubmittedDate}>
												{submittedDate.toLocaleDateString()} {submittedDate.toLocaleTimeString()}
											</span>
											<span className={style.ExpandIcon}>
												{isExpanded ? '▼' : '▶'}
											</span>
										</div>
									</div>
									
									{isExpanded && (
										<div className={style.SubmissionDetails}>
											<div className={style.AnswersList}>
												{submission.answers.map((answer, answerIdx) => {
													let displayValue = answer.value;
													
													if (answer.question.questionType === QuestionType.YES_NO) {
														displayValue = answer.value === 'yes' || answer.value === 'true' ? 'Yes' : 'No';
													} else if (answer.question.questionType === QuestionType.RATING) {
														const ratingNum = parseInt(answer.value);
														displayValue = `${ratingNum} - ${getRatingLabel(ratingNum)}`;
													}
													
													return (
														<div key={answerIdx} className={style.AnswerItem}>
															<div className={style.QuestionLabel}>
																Q{answer.question.sortOrder + 1}: {answer.question.questionText}
															</div>
															<div className={style.AnswerValue}>
																{displayValue}
															</div>
														</div>
													);
												})}
											</div>
											
											<div className={style.SubmissionFooter}>
												<div className={style.MetaInfo}>
													{submission.phone && (
														<span className={style.MetaItem}>
															<strong>Phone:</strong> {submission.phone}
														</span>
													)}
													<span className={style.MetaItem}>
														<strong>Device:</strong> {submission.device} ({submission.browser})
													</span>
													{submission.rewardOptIn && (
														<span className={style.MetaItem} style={{ color: '#28a745' }}>
															<strong>✓ Opted in for reward</strong>
														</span>
													)}
												</div>
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}
				{campaign.reward?.enabled && (
					<div className={style.RewardSection}>
						<h3>Reward Information</h3>
						<div className={style.RewardInfo}>
							<div className={style.RewardItem}>
								<span className={style.Label}>Reward:</span>
								<span className={style.Value}>{campaign.reward.rewardName}</span>
							</div>
							<div className={style.RewardItem}>
								<span className={style.Label}>Number of Winners:</span>
								<span className={style.Value}>{campaign.reward.numberOfWinners}</span>
							</div>
						</div>

						{winners && winners.length > 0 ? (
							<div className={style.WinnersList}>
								<h4>Contest Winners</h4>
								{winners.map((winner, index) => (
									<div key={winner.id} className={style.WinnerItem}>
										<span className={style.WinnerNumber}>#{index + 1}</span>
										<span className={style.WinnerName}>
											{winner.firstName && winner.lastName
												? `${winner.firstName} ${winner.lastName}`
												: 'Anonymous'}
										</span>
										{winner.email && <span className={style.WinnerEmail}>{winner.email}</span>}
									</div>
								))}
							</div>
						) : canPickWinners ? (
							<div className={style.PickWinnersSection}>
								<p>Campaign has ended. You can now select winners.</p>
								<button onClick={onPickWinners} className={style.PickWinnersButton}>
									Pick Winners Randomly
								</button>
							</div>
					) : !winners?.length && (results?.rewardOptInCount || 0) === 0 ? (
						<div className={style.NoWinnersMessage}>
							<p>No eligible submissions with reward opt-in yet.</p>
							<p>
								Eligible Submissions with Reward Opt-in: {results?.rewardOptInCount || 0}
							</p>
						</div>
					) : null}
				</div>
			)}
	</div>
	);
}
